import React, { useMemo } from "react";
import { FileSearch } from "lucide-react";
import Loader from "@/components/Loader";

const ALL_COLUMNS = [
  { key: "selection", label: "" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "phone_number", label: "Phone Number" },
  { key: "gender", label: "Gender" },
  { key: "sms_opt_in", label: "SMS" },
  { key: "email_opt_in", label: "Email" },
];

const MOBILE_FIELD_COLUMNS = [
  { key: "email", label: "Email" },
  { key: "phone_number", label: "Phone Number" },
  { key: "gender", label: "Gender" },
  { key: "sms_opt_in", label: "Opt SMS" },
  { key: "email_opt_in", label: "Opt Email" },
];

const isOptedIn = (value) => value === true || value === 1 || value === "1";

const OptInBadge = ({ optedIn }) => (
  <span
    className={`px-2 py-1 text-xs font-medium rounded-full ${
      optedIn
        ? "bg-green-500/10 text-green-600"
        : "bg-muted text-muted-foreground"
    }`}
  >
    {optedIn ? "Yes" : "No"}
  </span>
);

const MarketingTable = ({
  contacts,
  isLoading,
  searchTerm = "",
  selectedContacts = [],
  setSelectedContacts,
  visibleColumns = null,
}) => {
  const columns = useMemo(() => {
    return ALL_COLUMNS.filter((column) => {
      if (column.key === "selection") return true;
      if (!visibleColumns) return true;
      return visibleColumns[column.key] !== false;
    });
  }, [visibleColumns]);

  const mobileFields = useMemo(() => {
    return MOBILE_FIELD_COLUMNS.filter((column) => {
      if (!visibleColumns) return true;
      return visibleColumns[column.key] !== false;
    });
  }, [visibleColumns]);

  const showName = !visibleColumns || visibleColumns.name !== false;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedContacts(contacts.map((contact) => contact.id));
    } else {
      setSelectedContacts([]);
    }
  };

  const handleSelectContact = (id) => {
    setSelectedContacts((prev) =>
      prev.includes(id) ? prev.filter((contactId) => contactId !== id) : [...prev, id],
    );
  };

  const renderCell = (column, contact) => {
    switch (column.key) {
      case "selection":
        return (
          <input
            type="checkbox"
            checked={selectedContacts.includes(contact.id)}
            onChange={() => handleSelectContact(contact.id)}
            className="form-checkbox h-4 w-4 text-primary transition duration-150 ease-in-out"
          />
        );
      case "name":
        return (
          <div className="text-sm font-medium text-foreground">
            {contact.name || "-"}
          </div>
        );
      case "email":
        return (
          <div className="text-sm font-medium text-foreground break-all">
            {contact.email || "-"}
          </div>
        );
      case "phone_number":
        return (
          <div className="text-sm text-foreground">
            {contact.phone_number || "-"}
          </div>
        );
      case "gender":
        return (
          <div className="text-sm text-foreground">
            {contact.gender || "-"}
          </div>
        );
      case "sms_opt_in":
        return <OptInBadge optedIn={isOptedIn(contact.sms_opt_in)} />;
      case "email_opt_in":
        return <OptInBadge optedIn={isOptedIn(contact.email_opt_in)} />;
      default:
        return null;
    }
  };

  const renderMobileFieldValue = (column, contact) => {
    if (column.key === "sms_opt_in") {
      return <OptInBadge optedIn={isOptedIn(contact.sms_opt_in)} />;
    }
    if (column.key === "email_opt_in") {
      return <OptInBadge optedIn={isOptedIn(contact.email_opt_in)} />;
    }
    if (column.key === "email") {
      return contact.email || "-";
    }
    return contact[column.key] || "-";
  };

  const getCellClass = (column) => {
    if (column.key === "selection") {
      return "px-4 py-4 whitespace-nowrap text-center";
    }
    return "px-4 py-4 whitespace-nowrap";
  };

  if (!isLoading && !contacts.length) {
    return (
      <div className="text-center py-4 text-red-600 bg-red-200 rounded-lg">
        <div className="mb-2">
          <FileSearch className="h-12 w-12 mx-auto" />
        </div>
        <p className="text-sm">
          No marketing contacts found
          {searchTerm.trim() ? " for your search." : "."}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-card rounded-lg border border-border shadow">
      <div className="hidden md:block">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr className="border-b border-border">
              {columns.map((column) =>
                column.key === "selection" ? (
                  <th
                    key={column.key}
                    className="px-4 py-4 text-foreground align-center relative"
                  >
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={
                          selectedContacts.length === contacts.length &&
                          contacts.length > 0
                        }
                        onChange={handleSelectAll}
                        className="form-checkbox h-4 w-4 text-primary transition duration-150 ease-in-out"
                      />
                    </div>
                  </th>
                ) : (
                  <th
                    key={column.key}
                    className="px-4 pt-4 pb-2 text-foreground align-top relative"
                  >
                    <div className="mb-[10px] flex items-center">
                      <p className="mr-1 text-base font-medium text-muted-foreground whitespace-nowrap">
                        {column.label}
                      </p>
                    </div>
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="py-10">
                  <div className="flex justify-center items-center">
                    <Loader bgColor="bg-transparent" />
                  </div>
                </td>
              </tr>
            ) : (
              contacts.map((contact) => (
                <tr key={contact.id} className="hover:bg-muted">
                  {columns.map((column) => (
                    <td key={column.key} className={getCellClass(column)}>
                      {renderCell(column, contact)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden divide-y divide-border">
        {isLoading ? (
          <div className="py-10">
            <div className="flex justify-center items-center">
              <Loader bgColor="bg-transparent" />
            </div>
          </div>
        ) : (
          contacts.map((contact) => (
            <article key={contact.id} className="px-4 py-4 bg-card">
              <div className="flex items-start gap-3 pb-3 mb-3 border-b border-border">
                <div className="shrink-0 pt-0.5">
                  <input
                    type="checkbox"
                    checked={selectedContacts.includes(contact.id)}
                    onChange={() => handleSelectContact(contact.id)}
                    className="form-checkbox h-4 w-4 text-primary transition duration-150 ease-in-out"
                  />
                </div>
                {showName && (
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground">
                      {contact.name || "-"}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2.5">
                {mobileFields.map((column) => (
                  <div key={column.key} className="flex gap-3 items-start">
                    <span className="text-xs font-medium text-muted-foreground w-[40%] shrink-0 leading-snug pt-0.5">
                      {column.label}
                    </span>
                    <div
                      className={`flex-1 text-right min-w-0 text-sm text-foreground ${
                        column.key === "email" ? "break-all" : ""
                      }`}
                    >
                      {renderMobileFieldValue(column, contact)}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default MarketingTable;
